import { PrioritizedClientCaseDefinition, PrioritizedClientCaseResultV2 } from '@api/generated/models';
import { getAbsoluteRoute } from '@helpers/route';
import { AuthService } from '@services/auth/mod';
import { DateDisplayFormats, DateService } from '@services/date.service';
import { LicenceHolderService } from '@services/licence_holder.service';
import { UnitService } from '@services/unit.service';
import { CasesApiService } from '@src/api/cases_api.service';
import { Scoped } from '@visiba/instantiation';
import { Presentation } from '@visiba/presentation';
import { Timer } from '@visiba/std';

export interface AttentionCardAppointmentCaseModel {
	type: 'appointment';
	ticket: string;
	dateFormatted: string;
	dateInstance: VisibaDate;
	participantCount: number;
	actingHcp: Nullable<{ name: NullableString; profileImageUrl: NullableString }>;
	receptionName: string;
	link: string;
}

export interface AttentionCardMessageCaseModel {
	type: 'messaging';
	dateFormatted: string;
	dateInstance: VisibaDate;
	link: string;
	unreadCount: number;
	receptionName: Nullable<string>;
}

export type AttentionCardPresentationModel =
	| {
			type: PrioritizedClientCaseDefinition.None;
			appointmentCase: null;
			messageCase: null;
	  }
	| {
			type: PrioritizedClientCaseDefinition.NewMessages | PrioritizedClientCaseDefinition.NewMessagesDifferentCases;
			messageCase: AttentionCardMessageCaseModel;
	  }
	| {
			type:
				| PrioritizedClientCaseDefinition.CurrentAppointment
				| PrioritizedClientCaseDefinition.UpcomingAppointment
				| PrioritizedClientCaseDefinition.Unpaid;
			appointmentCase: AttentionCardAppointmentCaseModel;
	  }
	| {
			type: PrioritizedClientCaseDefinition.UnansweredForms;
			case: AttentionCardAppointmentCaseModel | AttentionCardMessageCaseModel;
			unansweredFormsNames: string[];
	  };

@Scoped()
export class AttentionCardController {
	private readonly timer = new Timer();
	public readonly presentation = Presentation.create<AttentionCardPresentationModel>({
		type: PrioritizedClientCaseDefinition.None,
		appointmentCase: null,
		messageCase: null,
	});

	constructor(
		private readonly casesApiService: CasesApiService,
		private readonly dateService: DateService,
		private readonly unitService: UnitService,
		// biome-ignore lint/correctness/noUnusedPrivateClassMembers: suppressed due to migration - should be fixed
		private readonly authService: AuthService,
		private readonly licenceHolderService: LicenceHolderService,
	) {
		this.timer.wait(() => {
			this.setAttention();
		}, 1000);
	}

	public teardown(): void {
		this.timer.flush();
	}

	public mapEnumToAnalyticEventAction(value: PrioritizedClientCaseDefinition): string | null {
		const leading = 'Presented with ';

		switch (value) {
			case PrioritizedClientCaseDefinition.CurrentAppointment:
				return leading + 'Go to lobby-card';
			case PrioritizedClientCaseDefinition.UpcomingAppointment:
				return leading + 'Next appointment-card';
			case PrioritizedClientCaseDefinition.Unpaid:
				return leading + 'Unpaid appointment-card';
			case PrioritizedClientCaseDefinition.NewMessages:
			case PrioritizedClientCaseDefinition.NewMessagesDifferentCases:
				return leading + 'Unread message-card';
			case PrioritizedClientCaseDefinition.UnansweredForms:
				return leading + 'Unanswered forms-card';
		}

		return null;
	}

	private async setAttention(): Promise<void> {
		try {
			const { data } = await this.casesApiService.getPrioritized();
			this.presentation.write(this.parseData(data));
		} catch (error: unknown) {
			console.log(error);
			// We'll just intentionally ignore this error and log.
			// TODO: log? idk＼（〇_ｏ）／
		}
	}

	private parseData(data: PrioritizedClientCaseResultV2): AttentionCardPresentationModel {
		if (data.CurrentAppointment != null) {
			const $case = this.parseAppointmentCase({
				countedParticipants: data.CurrentAppointment.ParticipantCount,
				goToDetailView: false,
				actingHcp:
					data.CurrentAppointment.ActingHcp != null
						? {
								name: data.CurrentAppointment.ActingHcp.Name,
								profileImageUrl: data.CurrentAppointment.ActingHcp.ProfileImageUrl,
							}
						: null,
				receptionName: data.CurrentAppointment.ReceptionName,
				ticket: data.CurrentAppointment.TicketId,
				timestamp: data.CurrentAppointment.Start,
				unitId: data.CurrentAppointment.UnitId,
			});

			return {
				appointmentCase: $case,
				type: PrioritizedClientCaseDefinition.CurrentAppointment,
			};
		}

		if (data.UnansweredForms != null) {
			const $case =
				data.UnansweredForms.Appointment != null
					? this.parseAppointmentCase({
							countedParticipants: data.UnansweredForms.Appointment.ParticipantCount,
							goToDetailView: true,
							actingHcp:
								data.UnansweredForms.Appointment.ActingHcp != null
									? {
											name: data.UnansweredForms.Appointment.ActingHcp.Name,
											profileImageUrl: data.UnansweredForms.Appointment.ActingHcp.ProfileImageUrl,
										}
									: null,
							receptionName: data.UnansweredForms.Appointment.ReceptionName,
							ticket: data.UnansweredForms.Appointment.TicketId,
							timestamp: data.UnansweredForms.Appointment.Start,
							unitId: data.UnansweredForms.Appointment.UnitId,
						})
					: this.parseMessageCase({
							caseId: data.UnansweredForms.Case?.CaseId ?? -1,
							// TODO PAT-426: Allow null?
							receptionName: data.UnansweredForms.Case?.ReceptionName ?? '',
							// TODO PAT-426: Allow null?
							unreadCount: data.UnansweredForms.Case?.UnreadCount ?? 0,
							// TODO PAT-426: Allow null?
							timestamp: data.UnansweredForms.Case?.LastMessageFromPeerTimestamp ?? '',
						});

			return {
				unansweredFormsNames: data.UnansweredForms.Forms ?? [],
				case: $case,
				type: PrioritizedClientCaseDefinition.UnansweredForms,
			};
		}

		if (data.Unpaid != null) {
			const $case = this.parseAppointmentCase({
				countedParticipants: data.Unpaid.ParticipantCount,
				goToDetailView: true,
				actingHcp:
					data.Unpaid.ActingHcp != null
						? {
								name: data.Unpaid.ActingHcp.Name,
								profileImageUrl: data.Unpaid.ActingHcp.ProfileImageUrl,
							}
						: null,
				receptionName: data.Unpaid.ReceptionName,
				ticket: data.Unpaid.TicketId,
				timestamp: data.Unpaid.Start,
				unitId: data.Unpaid.UnitId,
			});

			return {
				appointmentCase: $case,
				type: PrioritizedClientCaseDefinition.Unpaid,
			};
		}

		if (data.UpcomingAppointment != null) {
			const $case = this.parseAppointmentCase({
				countedParticipants: data.UpcomingAppointment.ParticipantCount,
				goToDetailView: true,
				actingHcp:
					data.UpcomingAppointment.ActingHcp != null
						? {
								name: data.UpcomingAppointment.ActingHcp.Name,
								profileImageUrl: data.UpcomingAppointment.ActingHcp.ProfileImageUrl,
							}
						: null,
				receptionName: data.UpcomingAppointment.ReceptionName,
				ticket: data.UpcomingAppointment.TicketId,
				timestamp: data.UpcomingAppointment.Start,
				unitId: data.UpcomingAppointment.UnitId,
			});

			return {
				appointmentCase: $case,
				type: PrioritizedClientCaseDefinition.UpcomingAppointment,
			};
		}

		if (data.NewMessage && data.NewMessage.UnreadCount > 0 && data.NewMessage.LastMessageFromPeerTimestamp != null) {
			const $case = this.parseMessageCase({
				caseId: data.NewMessage?.CaseId ?? -1,
				// TODO PAT-426: Allow null?
				receptionName: data.NewMessage?.ReceptionName ?? '',
				// TODO PAT-426: Allow null?
				unreadCount: data.NewMessage?.UnreadCount ?? 0,
				// TODO PAT-426: Allow null?
				timestamp: data.NewMessage?.LastMessageFromPeerTimestamp ?? '',
			});

			return {
				messageCase: $case,
				type: PrioritizedClientCaseDefinition.NewMessages,
			};
		}

		if (
			data.NewMessagesDifferentCases &&
			data.NewMessagesDifferentCases.UnreadMessagesCount > 0 &&
			data.NewMessagesDifferentCases.LastMessageFromPeerTimestamp != null
		) {
			const $case = this.parseMessageCase({
				// TODO PAT-426: Allow null?
				caseId: null,
				// TODO PAT-426: Allow null?
				receptionName: null,
				// TODO PAT-426: Allow null?
				unreadCount: data.NewMessagesDifferentCases?.UnreadMessagesCount ?? 0,
				// TODO PAT-426: Allow null?
				timestamp: data.NewMessagesDifferentCases?.LastMessageFromPeerTimestamp ?? '',
			});

			return {
				messageCase: $case,
				type: PrioritizedClientCaseDefinition.NewMessagesDifferentCases,
			};
		}

		return {
			type: PrioritizedClientCaseDefinition.None,
			appointmentCase: null,
			messageCase: null,
		};
	}

	private parseMessageCase(spec: {
		caseId: Nullable<number>;
		unreadCount: number;
		timestamp: string;
		receptionName: Nullable<string>;
	}): AttentionCardMessageCaseModel {
		const link =
			spec.caseId != null
				? `${this.licenceHolderService.alias}/visits/message/${spec.caseId}`
				: `${this.licenceHolderService.alias}/visits`;
		const date = this.dateService.create(spec.timestamp);

		return {
			type: 'messaging',
			receptionName: spec.receptionName ?? null,
			dateFormatted: date.format(
				this.dateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.FULL_DISPLAY_IN_CURRENT_YEAR),
			),
			dateInstance: date,
			link: getAbsoluteRoute(link),
			unreadCount: spec.unreadCount,
		};
	}

	private parseAppointmentCase(spec: {
		actingHcp: Nullable<{
			name: Nullable<string>;
			profileImageUrl: Nullable<string>;
		}>;
		countedParticipants: number;
		ticket: string;
		timestamp: string;
		receptionName: Nullable<string>;
		goToDetailView: boolean;
		unitId: number;
	}): AttentionCardAppointmentCaseModel {
		const unit = this.unitService.getUnitNodeInTree(spec.unitId);
		const link = spec.goToDetailView ? `${unit.route}/visits/video/${spec.ticket}` : `${unit.route}/appointment/${spec.ticket}`;
		const date = this.dateService.newDate(spec.timestamp);

		return {
			type: 'appointment',
			ticket: spec.timestamp,
			dateFormatted: date.format(
				this.dateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.FULL_DISPLAY_IN_CURRENT_YEAR),
			),
			dateInstance: date,
			actingHcp:
				spec.actingHcp != null
					? {
							name: spec.actingHcp.name,
							profileImageUrl: spec.actingHcp.profileImageUrl,
						}
					: null,
			participantCount: spec.countedParticipants,
			receptionName: spec.receptionName ?? '',
			link: getAbsoluteRoute(link),
		};
	}
}
